import { BaseTextMergeElement } from "./BaseTextMergeElement";
import { TextMergeType } from "../TextMergeType";
import { TextMergeConfiguration } from "../TextMergeConfiguration";
import { StateManager } from "../../Common/StateManager";
import { DataUtilities } from "../../Common/DataUtilities";
import { GetTextElementFromData } from "../RegisterTextElement";
import { ExecutionData } from "../../Common/Data/ExecutionData/ExecutionData";
import { ITestingContext } from "../../Common/Testing/ITestingContext";

export abstract class BaseTextMergeContainerElement extends BaseTextMergeElement {
    abstract readonly styleElement: boolean;

    private _bold: boolean = false;

    get bold(): boolean {
        return this._bold;
    }

    set bold(value: boolean) {
        this._bold = value;
        StateManager.propertyChanged(this, "bold");
    }

    private _italic: boolean = false;

    get italic(): boolean {
        return this._italic;
    }

    set italic(value: boolean) {
        this._italic = value;
        StateManager.propertyChanged(this, "italic");
    }

    private _underline: boolean = false;

    get underline(): boolean {
        return this._underline;
    }

    set underline(value: boolean) {
        this._underline = value;
        StateManager.propertyChanged(this, "underline");
    }

    private _fontSize?: number;

    get fontSize(): number | undefined {
        return this._fontSize;
    }

    set fontSize(value: number | undefined) {
        this._fontSize = value;
        StateManager.propertyChanged(this, "fontSize");
    }

    private _fontName?: string;

    get fontName(): string | undefined {
        return this._fontName;
    }

    set fontName(value: string | undefined) {
        this._fontName = value;
        StateManager.propertyChanged(this, "fontName");
    }

    private _alignment: "left" | "center" | "right" = "left";

    get alignment(): "left" | "center" | "right" {
        return this._alignment;
    }

    set alignment(value: "left" | "center" | "right") {
        this._alignment = value;
        StateManager.propertyChanged(this, "alignment");
    }

    private _fontColor?: string;

    get fontColor(): string | undefined {
        return this._fontColor;
    }

    set fontColor(value: string | undefined) {
        this._fontColor = value;
        StateManager.propertyChanged(this, "fontColor");
    }

    private _backgroundColor?: string;

    get backgroundColor(): string | undefined {
        return this._backgroundColor;
    }

    set backgroundColor(value: string | undefined) {
        this._backgroundColor = value;
        StateManager.propertyChanged(this, "backgroundColor");
    }

    private _elements: BaseTextMergeElement[] = [];

    get elements(): readonly BaseTextMergeElement[] {
        this._elements.forEach((e) => {
            e.parent = this;
        });
        return this._elements;
    }

    private _margin: number = 5;

    get margin(): number {
        return this._margin;
    }

    set margin(value: number) {
        this._margin = value;
        StateManager.propertyChanged(this, "margin");
    }

    abstract getHTMLTag(): string;

    addElement(e: BaseTextMergeElement, index?: number) {
        e.parent = this;
        if (index === undefined) {
            this._elements.push(e);
            StateManager.propertyChanged(this, "elements");
        } else {
            this._elements.splice(index, 0, e);
            StateManager.propertyChanged(this, "elements");
        }
    }

    removeElement(e: BaseTextMergeElement) {
        const index = this._elements.indexOf(e, 0);
        if (index > -1) {
            this._elements.splice(index, 1);
        }
        StateManager.propertyChanged(this, "elements");
    }

    removeElementAt(index: number) {
        if (index > -1) {
            this._elements.splice(index, 1);
        }
        StateManager.propertyChanged(this, "elements");
    }

    removeAllElements() {
        this._elements = [];
    }

    getStorageData(): object {
        let d = super.getStorageData();

        DataUtilities.addStorageData(
            this,
            d,
            "bold",
            "italic",
            "underline",
            "fontSize",
            "fontName",
            "alignment",
            "fontColor",
            "backgroundColor",
            "margin"
        );
        let contained: object[] = [];

        for (let e of this._elements) contained.push(e.getStorageData());

        Reflect.set(d, "elements", contained);

        return d;
    }

    setStorageData(d: object) {
        super.setStorageData(d);

        DataUtilities.setStorageData(
            this,
            d,
            "bold",
            "italic",
            "underline",
            "fontSize",
            "fontName",
            "alignment",
            "fontColor",
            "backgroundColor",
            "margin"
        );

        let edata = Reflect.get(d, "elements");

        for (let e of edata) {
            let el = GetTextElementFromData(e);
            if (el) {
                el.parent = this;
                this._elements.push(el);
            }
        }
    }

    getStyles(): { styleObject: object; styles: string } {
        let styles = {};
        let styleString = " ";

        if (this.bold !== undefined) {
            Reflect.set(styles, "fontWeight", this.bold ? "bold" : "normal");
            styleString = styleString + "font-weight:" + Reflect.get(styles, "fontWeight") + "; ";
        }

        if (this.backgroundColor !== undefined) {
            Reflect.set(styles, "backgroundColor", this.backgroundColor);
            styleString = styleString + "background-color:" + Reflect.get(styles, "backgroundColor") + "; ";
        }

        if (this.italic !== undefined) {
            Reflect.set(styles, "fontStyle", this.italic ? "italic" : "normal");
            styleString = styleString + "font-style:" + Reflect.get(styles, "fontStyle") + "; ";
        }

        if (this.underline !== undefined) {
            Reflect.set(styles, "textDecorationLine", this.underline ? "underline" : "none");
            styleString = styleString + "text-decoration-line:" + Reflect.get(styles, "textDecorationLine") + "; ";
        }

        if (this.fontColor !== undefined) {
            Reflect.set(styles, "color", this.fontColor);
            styleString = styleString + "color:" + Reflect.get(styles, "color") + "; ";
        }

        if (this.fontName !== undefined) {
            Reflect.set(styles, "fontFamily", this.fontName);
            styleString = styleString + "font-family:" + Reflect.get(styles, "fontFamily") + "; ";
        }

        if (this.fontSize !== undefined) {
            Reflect.set(styles, "fontSize", this.fontSize + "px");
            styleString = styleString + "font-size:" + Reflect.get(styles, "fontSize") + "; ";
        }

        if (this.alignment !== undefined) {
            Reflect.set(styles, "textAlign", this.alignment);
            styleString = styleString + "text-align:" + Reflect.get(styles, "textAlign") + "; ";
        }

        if (this.margin !== undefined) {
            Reflect.set(styles, "marginTop", this.margin + "px");
            Reflect.set(styles, "marginBottom", this.margin + "px");
            styleString = styleString + "margin-top:" + Reflect.get(styles, "marginTop") + "; ";
            styleString = styleString + "margin-bottom:" + Reflect.get(styles, "marginBottom") + "; ";
        }
        return { styleObject: styles, styles: styleString };
    }

    output(data: ExecutionData, config: TextMergeConfiguration, testContext: ITestingContext | undefined): string {
        // do not add styles
        if (!this.styleElement || config.type !== TextMergeType.HTML) {
            return this.elements.map((e) => e.output(data, config, testContext)).join(" ");
        } else {
            let output: string;

            if (this.getHTMLTag() === "") {
                output = "";
            } else {
                output = "<" + this.getHTMLTag() + ' style="' + this.getStyles().styles + '">';
            }

            output = output + this.elements.map((e) => e.output(data, config, testContext)).join(" ");

            if (this.getHTMLTag() !== "") {
                output = output + "</" + this.getHTMLTag() + ">";
            }

            return output;
        }
    }
}
